/**
 * Procedural outdoor props (+field): trees, bushes, flowers, rocks, fences, cottages, stalls, a
 * well, lanterns, banners, bandit tents, standing stones, crystals, boards and the barrow mound.
 * Chunky anime-toon silhouettes from primitives with baked vertex colours (painted AO: darker at
 * the base, lighter on top), like themes/venomAltar/procs.ts. Every generator returns a
 * non-indexed BufferGeometry in yards (position, normal, color), origin at the base centre,
 * facing +z. The field builder merges them per chunk on the prop atlas (white texel), so props
 * cost no extra draw calls.
 *
 * Ids are `name` or `name#variant` (variants reseed the lumps / proportions).
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { hashString, seededRandom } from '../layout';

type Col = number | readonly [number, number, number];

/** Outdoor palette (sRGB hex). Bright, saturated, but the ground stays mid-value. */
export const OUT = {
  bark: 0x6e4b33,
  barkDark: 0x4f3524,
  leafMeadow: 0x4d9437,
  leafMeadowLight: 0x78b24a,
  leafForest: 0x356f33,
  leafForestDark: 0x23502a,
  pine: 0x275a3b,
  pineLight: 0x3f7f4c,
  blossom: 0xf2a6c2,
  rock: 0x9b9a92,
  rockDark: 0x6e6d68,
  moss: 0x6f9a4c,
  plaster: 0xf0e4cb,
  plasterWarm: 0xe8cfa6,
  timber: 0x5c3d27,
  roofRed: 0xb4533b,
  roofSlate: 0x56688a,
  roofThatch: 0xc8a056,
  wood: 0x9c7448,
  woodDark: 0x6b4d30,
  iron: 0x3d3d44,
  gold: 0xd9a845,
  cloth: 0xb73c3c,
  clothBlue: 0x3a64a8,
  clothCream: 0xeee0c0,
  canvas: 0x9c8460,
  canvasDark: 0x6e5a3e,
  bramble: 0x3b5b2e,
  thorn: 0x5a3a46,
  stone: 0xa9a49a,
  stoneMoss: 0x7f8f6c,
  crystal: 0x6fe3e0,
  hay: 0xd8b860,
  water: 0x3f7fa0,
} as const;

const tmpC = new THREE.Color();
const lin = (c: Col): [number, number, number] => {
  if (typeof c === 'number') {
    tmpC.setHex(c);
    return [tmpC.r, tmpC.g, tmpC.b];
  }
  return [c[0], c[1], c[2]];
};

/** Bake a colour with a vertical AO gradient over [y0, y1] into a non-indexed copy. */
export function paint(geo: THREE.BufferGeometry, color: Col, ao: readonly [number, number] = [0.72, 1.08], range?: readonly [number, number]): THREE.BufferGeometry {
  if (!geo.attributes['normal']) geo.computeVertexNormals();
  const g = geo.index ? geo.toNonIndexed() : geo;
  if (g !== geo) geo.dispose();
  g.deleteAttribute('uv');
  const pos = g.attributes['position'] as THREE.BufferAttribute;
  g.computeBoundingBox();
  const y0 = range ? range[0] : g.boundingBox!.min.y;
  const y1 = range ? range[1] : g.boundingBox!.max.y;
  const [r, gg, b] = lin(color);
  const col = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const t = y1 > y0 ? Math.min(1, Math.max(0, (pos.getY(i) - y0) / (y1 - y0))) : 1;
    const k = ao[0] + (ao[1] - ao[0]) * t;
    col[i * 3] = r * k;
    col[i * 3 + 1] = gg * k;
    col[i * 3 + 2] = b * k;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return g;
}

const M = new THREE.Matrix4();
const Q = new THREE.Quaternion();
const E = new THREE.Euler();
const S = new THREE.Vector3();
const T = new THREE.Vector3();
/** Transform helper: scale, rotate (euler XYZ), translate. */
export function xf(geo: THREE.BufferGeometry, t: readonly [number, number, number], r: readonly [number, number, number] = [0, 0, 0], s: readonly [number, number, number] = [1, 1, 1]): THREE.BufferGeometry {
  M.compose(T.set(t[0], t[1], t[2]), Q.setFromEuler(E.set(r[0], r[1], r[2])), S.set(s[0], s[1], s[2]));
  geo.applyMatrix4(M);
  return geo;
}

export function combine(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const g = mergeGeometries(parts, false)!;
  parts.forEach((p) => p.dispose());
  return g;
}

const box = (w: number, h: number, d: number): THREE.BufferGeometry => new THREE.BoxGeometry(w, h, d).translate(0, h / 2, 0);
const cyl = (rt: number, rb: number, h: number, seg = 10): THREE.BufferGeometry => new THREE.CylinderGeometry(rt, rb, h, seg).translate(0, h / 2, 0);
const cone = (r: number, h: number, seg = 9): THREE.BufferGeometry => new THREE.ConeGeometry(r, h, seg).translate(0, h / 2, 0);

/** A lumpy smooth blob (canopies, bushes, rocks): a low-poly sphere pushed out by value noise. */
export function blob(r: number, rnd: () => number, lump = 0.18, ws = 9, hs = 7, flatBottom = 0): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(r, ws, hs);
  const p = g.attributes['position'] as THREE.BufferAttribute;
  const ox = rnd() * 10;
  const oz = rnd() * 10;
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i);
    const y = p.getY(i);
    const z = p.getZ(i);
    const n = Math.sin(x * 2.1 / r + ox) * Math.cos(z * 1.9 / r + oz) * 0.5 + Math.sin(y * 2.7 / r + ox * 0.7) * 0.5;
    const k = 1 + lump * n;
    let yy = y * k;
    if (flatBottom > 0 && yy < -r * flatBottom) yy = -r * flatBottom + (yy + r * flatBottom) * 0.15;
    p.setXYZ(i, x * k, yy, z * k);
  }
  g.computeVertexNormals();
  return g;
}

/** A rock: an icosahedron with flat facets (reads as stone, not as a bush). */
export function rockGeo(r: number, rnd: () => number, squash = 0.6): THREE.BufferGeometry {
  const g = new THREE.IcosahedronGeometry(r, 1);
  const p = g.attributes['position'] as THREE.BufferAttribute;
  // Displace shared positions consistently (non-indexed: hash the rounded position).
  const seen = new Map<string, number>();
  for (let i = 0; i < p.count; i++) {
    const key = `${p.getX(i).toFixed(3)},${p.getY(i).toFixed(3)},${p.getZ(i).toFixed(3)}`;
    let k = seen.get(key);
    if (k === undefined) {
      k = 0.78 + rnd() * 0.42;
      seen.set(key, k);
    }
    const y = p.getY(i) * k * squash;
    p.setXYZ(i, p.getX(i) * k, Math.max(-r * 0.15, y), p.getZ(i) * k);
  }
  g.computeVertexNormals();
  return g.translate(0, r * 0.12, 0);
}

export type Gen = (rnd: () => number) => THREE.BufferGeometry;

// ---------------------------------------------------------------------------------------------
// Vegetation
// ---------------------------------------------------------------------------------------------

/** Round broadleaf tree: a fat trunk and a cloud of 4–6 canopy blobs (meadow / town). */
function roundTree(leaf: number, leafLight: number, height: number): Gen {
  return (rnd) => {
    const parts: THREE.BufferGeometry[] = [];
    const th = height * 0.42;
    parts.push(paint(cyl(0.22, 0.36, th, 8), OUT.bark, [0.55, 1.0]));
    // Root flare and two stubby boughs.
    parts.push(paint(xf(cone(0.62, 0.55, 8), [0, 0, 0]), OUT.barkDark, [0.6, 0.9]));
    parts.push(paint(xf(cyl(0.08, 0.14, 1.3, 6), [0.25, th * 0.75, 0], [0, 0, -0.8]), OUT.bark, [0.7, 1]));
    parts.push(paint(xf(cyl(0.08, 0.13, 1.2, 6), [-0.2, th * 0.85, 0.1], [0.2, 0, 0.75]), OUT.bark, [0.7, 1]));
    const n = 4 + Math.floor(rnd() * 3);
    const cy = th + height * 0.22;
    const R = height * 0.24;
    const y0 = th - R * 0.2;
    const y1 = height;
    parts.push(paint(xf(blob(R * 1.2, rnd, 0.16), [0, cy + R * 0.25, 0], [0, 0, 0], [1, 0.85, 1]), leaf, [0.62, 1.12], [y0, y1]));
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + rnd() * 0.6;
      const d = R * (0.7 + rnd() * 0.35);
      const r = R * (0.7 + rnd() * 0.3);
      const y = cy + (rnd() - 0.35) * R * 0.8;
      parts.push(paint(xf(blob(r, rnd, 0.2), [Math.cos(a) * d, y, Math.sin(a) * d], [0, 0, 0], [1, 0.82, 1]), i % 2 ? leaf : leafLight, [0.6, 1.1], [y0, y1]));
    }
    parts.push(paint(xf(blob(R * 0.8, rnd, 0.2), [0, cy + R * 0.95, 0]), leafLight, [0.85, 1.15], [y0, y1]));
    return combine(parts);
  };
}

/** Pine: a slim trunk and 4 stacked, slightly drooping cones (forest edges). */
function pineTree(height: number): Gen {
  return (rnd) => {
    const parts: THREE.BufferGeometry[] = [paint(cyl(0.16, 0.3, height * 0.35, 7), OUT.barkDark, [0.55, 1.0])];
    const tiers = 4;
    for (let i = 0; i < tiers; i++) {
      const t = i / tiers;
      const r = height * (0.3 - t * 0.2) * (0.9 + rnd() * 0.2);
      const h = height * 0.34;
      const y = height * 0.2 + t * height * 0.56;
      const c = new THREE.ConeGeometry(r, h, 9, 1);
      // Droop the rim a little so tiers overlap like boughs.
      const p = c.attributes['position'] as THREE.BufferAttribute;
      for (let k = 0; k < p.count; k++) if (p.getY(k) < -h * 0.45) p.setY(k, p.getY(k) - 0.18 * (0.5 + rnd()));
      c.computeVertexNormals();
      parts.push(paint(xf(c, [0, y + h / 2, 0], [0, rnd() * 3, 0]), i % 2 ? OUT.pine : OUT.pineLight, [0.62, 1.08]));
    }
    return combine(parts);
  };
}

/** Tall forest oak: darker, bigger canopy, a twisted trunk. */
const oakTree = (height: number): Gen => roundTree(OUT.leafForest, OUT.leafForestDark, height);

function bush(leaf: number, light: number, r0: number): Gen {
  return (rnd) => {
    const parts: THREE.BufferGeometry[] = [];
    const n = 3 + Math.floor(rnd() * 2);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + rnd();
      const d = r0 * 0.55;
      const r = r0 * (0.6 + rnd() * 0.3);
      parts.push(paint(xf(blob(r, rnd, 0.22, 8, 6, 0.3), [Math.cos(a) * d, r * 0.55, Math.sin(a) * d]), i % 2 ? leaf : light, [0.6, 1.12], [0, r0 * 1.5]));
    }
    parts.push(paint(xf(blob(r0 * 0.75, rnd, 0.2, 8, 6, 0.3), [0, r0 * 0.9, 0]), light, [0.8, 1.15], [0, r0 * 1.6]));
    return combine(parts);
  };
}

/** A flower patch: leafy tufts with tiny bright heads (detail layer). */
function flowers(colors: readonly number[]): Gen {
  return (rnd) => {
    const parts: THREE.BufferGeometry[] = [];
    const n = 7 + Math.floor(rnd() * 5);
    for (let i = 0; i < n; i++) {
      const a = rnd() * Math.PI * 2;
      const d = Math.sqrt(rnd()) * 0.75;
      const x = Math.cos(a) * d;
      const z = Math.sin(a) * d;
      const h = 0.28 + rnd() * 0.22;
      parts.push(paint(xf(cyl(0.015, 0.02, h, 4), [x, 0, z]), 0x3f7a2c, [0.8, 1]));
      const c = colors[i % colors.length]!;
      parts.push(paint(xf(new THREE.IcosahedronGeometry(0.075 + rnd() * 0.03, 0), [x, h, z], [rnd(), rnd(), 0], [1, 0.7, 1]), c, [1, 1.1]));
    }
    for (let i = 0; i < 3; i++) {
      const a = rnd() * Math.PI * 2;
      parts.push(paint(xf(blob(0.22, rnd, 0.25, 6, 4, 0.4), [Math.cos(a) * 0.35, 0.06, Math.sin(a) * 0.35], [0, 0, 0], [1, 0.5, 1]), 0x5aa83a, [0.7, 1.1]));
    }
    return combine(parts);
  };
}

/** Bramble: a dark thorny tangle with a few purple-red thorns and berries. */
const bramble: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + rnd();
    parts.push(paint(xf(blob(0.55 + rnd() * 0.25, rnd, 0.3, 7, 5, 0.3), [Math.cos(a) * 0.45, 0.4, Math.sin(a) * 0.45], [0, 0, 0], [1, 0.75, 1]), i % 2 ? OUT.bramble : 0x4a6a34, [0.55, 1.05], [0, 1.2]));
  }
  for (let i = 0; i < 9; i++) {
    const a = rnd() * Math.PI * 2;
    const r = 0.5 + rnd() * 0.5;
    const tilt = 0.6 + rnd() * 0.6;
    parts.push(paint(xf(cone(0.05, 0.42, 4), [Math.cos(a) * r, 0.45 + rnd() * 0.4, Math.sin(a) * r], [Math.sin(a) * tilt, 0, -Math.cos(a) * tilt]), OUT.thorn, [0.8, 1.1]));
  }
  for (let i = 0; i < 5; i++) {
    const a = rnd() * Math.PI * 2;
    parts.push(paint(xf(new THREE.IcosahedronGeometry(0.07, 0), [Math.cos(a) * 0.7, 0.6 + rnd() * 0.3, Math.sin(a) * 0.7]), 0x8a2a4a, [1, 1]));
  }
  return combine(parts);
};

/** Stump with a pale cut top. */
const stump: Gen = (rnd) => {
  const r = 0.38 + rnd() * 0.1;
  return combine([paint(cyl(r, r * 1.25, 0.55, 9), OUT.bark, [0.6, 0.95]), paint(xf(cyl(r * 0.96, r * 0.96, 0.04, 9), [0, 0.55, 0]), 0xd8b98a, [1, 1]), paint(cone(r * 1.6, 0.25, 9), OUT.barkDark, [0.7, 0.9])]);
};

/** Fallen log. */
const log: Gen = (rnd) => {
  const L = 2.4 + rnd() * 0.8;
  const g = new THREE.CylinderGeometry(0.3, 0.34, L, 9).rotateZ(Math.PI / 2).translate(0, 0.3, 0);
  return combine([paint(g, OUT.bark, [0.6, 1.0]), paint(xf(new THREE.CylinderGeometry(0.27, 0.27, 0.04, 9).rotateZ(Math.PI / 2), [L / 2 + 0.01, 0.3, 0]), 0xd8b98a, [1, 1])]);
};

/** Mushroom cluster (forest floor). */
const mushrooms: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 4; i++) {
    const a = rnd() * Math.PI * 2;
    const d = rnd() * 0.35;
    const h = 0.18 + rnd() * 0.2;
    const x = Math.cos(a) * d;
    const z = Math.sin(a) * d;
    parts.push(paint(xf(cyl(0.035, 0.05, h, 5), [x, 0, z]), 0xeae0c8, [0.8, 1]));
    parts.push(paint(xf(new THREE.SphereGeometry(0.11 + rnd() * 0.06, 7, 4, 0, Math.PI * 2, 0, Math.PI / 2), [x, h - 0.02, z]), i % 2 ? 0xc0463a : 0xd88a3a, [0.9, 1.1]));
  }
  return combine(parts);
};

// ---------------------------------------------------------------------------------------------
// Stone
// ---------------------------------------------------------------------------------------------

const rock = (r: number, squash: number, color: number = OUT.rock): Gen => (rnd) => combine([paint(rockGeo(r, rnd, squash), color, [0.62, 1.08])]);

/** Boulder with a mossy cap. */
const boulder: Gen = (rnd) => {
  const g = rockGeo(1.4, rnd, 0.7);
  const cap = paint(xf(blob(0.55, rnd, 0.35, 7, 5, 0.6), [0.35, 0.9, -0.2], [0.2, 0, 0.15], [1.0, 0.3, 0.8]), OUT.moss, [0.85, 1.05]);
  const chip = paint(rockGeo(0.6, rnd, 0.6).translate(1.1, 0, 0.7), OUT.rock, [0.6, 1.05]);
  return combine([paint(g, OUT.rock, [0.55, 1.05]), cap, chip]);
};

/** A standing stone (barrow ring, meadow landmarks): tall, slightly leaning, mossy foot. */
const standingStone: Gen = (rnd) => {
  const h = 3.2 + rnd() * 1.2;
  const g = new THREE.BoxGeometry(1.0, h, 0.6, 1, 3, 1);
  const p = g.attributes['position'] as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i++) {
    const t = (p.getY(i) + h / 2) / h;
    const k = 1 - 0.3 * t;
    p.setX(i, p.getX(i) * k + (rnd() - 0.5) * 0.08);
    p.setZ(i, p.getZ(i) * k);
  }
  g.translate(0, h / 2, 0).rotateZ((rnd() - 0.5) * 0.12);
  g.computeVertexNormals();
  return combine([paint(g, OUT.stone, [0.6, 1.05]), paint(xf(blob(0.75, rnd, 0.2, 7, 5, 0.5), [0, 0.1, 0], [0, 0, 0], [1.1, 0.3, 0.9]), OUT.moss, [0.8, 1])]);
};

/** A glowing crystal cluster (the albedo stays mid; the glow is a separate additive card). */
const crystals: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [paint(rockGeo(0.6, rnd, 0.4), OUT.rockDark, [0.6, 1])];
  const n = 4 + Math.floor(rnd() * 3);
  for (let i = 0; i < n; i++) {
    const a = rnd() * Math.PI * 2;
    const h = 0.9 + rnd() * 1.1;
    const tilt = 0.15 + rnd() * 0.45;
    const c = new THREE.OctahedronGeometry(0.22, 0).scale(1, h / 0.44, 1);
    parts.push(paint(xf(c, [Math.cos(a) * 0.25, h * 0.45, Math.sin(a) * 0.25], [Math.sin(a) * tilt, rnd(), -Math.cos(a) * tilt]), OUT.crystal, [0.85, 1.25]));
  }
  return combine(parts);
};

// ---------------------------------------------------------------------------------------------
// Town
// ---------------------------------------------------------------------------------------------

/** A fence segment, 3 yd along x, centred: two posts, two rails. */
const fence: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  for (const x of [-1.5, 1.5]) parts.push(paint(xf(box(0.18, 1.25, 0.18), [x, 0, 0]), OUT.woodDark, [0.6, 1]));
  for (const y of [0.45, 0.95]) parts.push(paint(xf(box(3.1, 0.12, 0.08), [0, y, 0], [0, 0, (rnd() - 0.5) * 0.06]), OUT.wood, [0.85, 1.05]));
  return combine(parts);
};

/** A low hedge, 3 yd long (town edges, gardens). */
const hedge: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 4; i++) parts.push(paint(xf(blob(0.75, rnd, 0.18, 8, 6, 0.6), [-1.15 + i * 0.77, 0.6, (rnd() - 0.5) * 0.15], [0, 0, 0], [1, 0.95, 0.85]), i % 2 ? OUT.leafForest : 0x4f9640, [0.55, 1.08], [0, 1.4]));
  return combine(parts);
};

/** A timber-framed cottage (~6 × 5 yd footprint): plaster walls, beams, a steep roof, a chimney. */
function cottage(roof: number, plaster: number): Gen {
  return (rnd) => {
    const W = 5.6 + rnd() * 1.2;
    const D = 4.4 + rnd() * 0.8;
    const H = 2.9;
    const parts: THREE.BufferGeometry[] = [];
    // Stone foundation, plaster walls.
    parts.push(paint(box(W + 0.3, 0.5, D + 0.3), OUT.stone, [0.7, 0.95]));
    parts.push(paint(xf(box(W, H, D), [0, 0.5, 0]), plaster, [0.82, 1.05]));
    // Timber frame: corner posts, a sill and a top plate, cross braces on the gable sides.
    for (const sx of [-1, 1])
      for (const sz of [-1, 1]) parts.push(paint(xf(box(0.24, H + 0.05, 0.24), [sx * (W / 2), 0.5, sz * (D / 2)]), OUT.timber, [0.7, 1]));
    for (const sz of [-1, 1]) {
      parts.push(paint(xf(box(W + 0.1, 0.2, 0.12), [0, 0.5 + H - 0.2, sz * (D / 2 + 0.02)]), OUT.timber, [0.8, 1]));
      parts.push(paint(xf(box(W + 0.1, 0.16, 0.12), [0, 1.65, sz * (D / 2 + 0.02)]), OUT.timber, [0.8, 1]));
      parts.push(paint(xf(box(0.16, H, 0.12), [-W / 4, 0.5, sz * (D / 2 + 0.02)]), OUT.timber, [0.8, 1]));
      parts.push(paint(xf(box(0.16, H, 0.12), [W / 4, 0.5, sz * (D / 2 + 0.02)]), OUT.timber, [0.8, 1]));
    }
    // Door (front, +z) and windows with shutters.
    parts.push(paint(xf(box(1.1, 1.95, 0.12), [0.15, 0.5, D / 2 + 0.06]), OUT.woodDark, [0.7, 1]));
    parts.push(paint(xf(box(1.35, 0.14, 0.16), [0.15, 2.45, D / 2 + 0.08]), OUT.timber, [1, 1]));
    for (const wx of [-W / 2 + 1.0, W / 2 - 1.0]) {
      if (Math.abs(wx - 0.15) < 1.2) continue;
      parts.push(paint(xf(box(0.9, 0.85, 0.08), [wx, 1.75, D / 2 + 0.05]), 0x2c3a52, [1, 1]));
      parts.push(paint(xf(box(1.05, 0.1, 0.14), [wx, 1.68, D / 2 + 0.08]), OUT.timber, [1, 1]));
      for (const s of [-1, 1]) parts.push(paint(xf(box(0.36, 0.95, 0.06), [wx + s * 0.66, 1.7, D / 2 + 0.07]), roof, [0.7, 0.95]));
    }
    for (const sx of [-1, 1]) parts.push(paint(xf(box(0.08, 0.85, 0.9), [sx * (W / 2 + 0.05), 1.75, 0]), 0x2c3a52, [1, 1]));
    // Steep roof: two slabs meeting at the ridge, overhanging; gable triangles.
    const rise = 2.3;
    const half = D / 2 + 0.55;
    const slope = Math.hypot(half, rise);
    const ang = Math.atan2(rise, half);
    for (const sz of [-1, 1]) {
      const slab = new THREE.BoxGeometry(W + 0.9, 0.22, slope + 0.1);
      parts.push(paint(xf(slab, [0, 0.5 + H + rise / 2, sz * (half / 2)], [sz * ang, 0, 0]), roof, [0.72, 1.06]));
    }
    parts.push(paint(xf(box(W + 1.0, 0.18, 0.3), [0, 0.5 + H + rise - 0.02, 0]), roof, [0.6, 0.8]));
    const gable = new THREE.BufferGeometry();
    const gp = [-D / 2, 0, 0, D / 2, 0, 0, 0, rise - 0.1, 0];
    gable.setAttribute('position', new THREE.Float32BufferAttribute(gp, 3));
    gable.computeVertexNormals();
    for (const sx of [-1, 1]) {
      const g = gable.clone();
      g.rotateY(sx * Math.PI / 2).translate(sx * (W / 2), 0.5 + H, 0);
      // Make both faces (simple: duplicate reversed).
      const p = g.attributes['position'] as THREE.BufferAttribute;
      const rev = new THREE.BufferGeometry();
      rev.setAttribute('position', new THREE.Float32BufferAttribute([p.getX(0), p.getY(0), p.getZ(0), p.getX(2), p.getY(2), p.getZ(2), p.getX(1), p.getY(1), p.getZ(1)], 3));
      rev.computeVertexNormals();
      parts.push(paint(g, plaster, [0.9, 1.05], [0.5 + H, 0.5 + H + rise]), paint(rev, plaster, [0.9, 1.05], [0.5 + H, 0.5 + H + rise]));
    }
    gable.dispose();
    // Chimney.
    const cx = (rnd() < 0.5 ? -1 : 1) * (W / 2 - 1.1);
    parts.push(paint(xf(box(0.8, 2.6, 0.8), [cx, 0.5 + H + 0.6, -0.5]), OUT.stone, [0.7, 1.05]));
    parts.push(paint(xf(box(0.95, 0.2, 0.95), [cx, 0.5 + H + 3.2, -0.5]), OUT.rockDark, [1, 1]));
    // Flower box under a front window.
    parts.push(paint(xf(box(1.0, 0.25, 0.3), [-W / 2 + 1.0, 1.25, D / 2 + 0.2]), OUT.woodDark, [0.8, 1]));
    for (let i = 0; i < 4; i++) parts.push(paint(xf(new THREE.IcosahedronGeometry(0.1, 0), [-W / 2 + 0.65 + i * 0.24, 1.55, D / 2 + 0.22]), [0xf2c94c, 0xe85a6a, 0xf2f2f2, 0xb07ae8][i]!, [1, 1.1]));
    return combine(parts);
  };
}

/** Market stall: counter, four posts, a striped cloth canopy, goods on the counter. */
function stall(a: number, b: number): Gen {
  return (rnd) => {
    const parts: THREE.BufferGeometry[] = [];
    const W = 3.2;
    const D = 1.8;
    parts.push(paint(xf(box(W, 1.0, 0.9), [0, 0, 0.35]), OUT.wood, [0.7, 1.0]));
    parts.push(paint(xf(box(W + 0.15, 0.1, 1.05), [0, 1.0, 0.35]), OUT.woodDark, [1, 1]));
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) parts.push(paint(xf(box(0.14, sz > 0 ? 2.5 : 2.9, 0.14), [sx * (W / 2), 0, sz * (D / 2)]), OUT.woodDark, [0.7, 1]));
    // Sloped striped canopy (5 stripes).
    for (let i = 0; i < 5; i++) {
      const s = new THREE.BoxGeometry(W / 5 + 0.02, 0.08, D + 0.7);
      parts.push(paint(xf(s, [-W / 2 + (i + 0.5) * (W / 5), 2.75, 0.05], [0.22, 0, 0]), i % 2 ? a : b, [0.9, 1.05]));
    }
    // Scalloped front valance.
    for (let i = 0; i < 6; i++) parts.push(paint(xf(new THREE.CylinderGeometry(0.28, 0.28, 0.06, 8, 1, false, 0, Math.PI).rotateX(Math.PI / 2), [-W / 2 + 0.27 + i * 0.53, 2.48, D / 2 + 0.38]), i % 2 ? b : a, [1, 1]));
    // Goods: crates of produce.
    for (let i = 0; i < 3; i++) {
      const x = -1.0 + i;
      parts.push(paint(xf(box(0.6, 0.3, 0.45), [x, 1.05, 0.35]), OUT.wood, [0.8, 1]));
      for (let k = 0; k < 4; k++) parts.push(paint(xf(new THREE.IcosahedronGeometry(0.11, 0), [x - 0.15 + (k % 2) * 0.3, 1.38, 0.25 + Math.floor(k / 2) * 0.2]), [0xd94a3a, 0xf0c040, 0x7ac040][i]!, [0.9, 1.1]));
    }
    if (rnd() < 0.5) parts.push(paint(xf(cyl(0.3, 0.3, 0.7, 10), [W / 2 + 0.5, 0, 0.6]), OUT.wood, [0.6, 1]));
    return combine(parts);
  };
}

/** Village well: stone ring, two posts, a little roof, crank and bucket. */
const well: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const ring = new THREE.CylinderGeometry(1.15, 1.25, 0.95, 14, 1, true);
  parts.push(paint(xf(ring, [0, 0.475, 0]), OUT.stone, [0.65, 1.0]));
  parts.push(paint(xf(new THREE.CylinderGeometry(0.92, 0.92, 0.9, 14, 1, true).scale(-1, 1, 1), [0, 0.45, 0]), OUT.rockDark, [0.4, 0.8]));
  parts.push(paint(xf(new THREE.TorusGeometry(1.08, 0.16, 6, 16).rotateX(Math.PI / 2), [0, 0.98, 0]), OUT.stoneMoss, [1, 1.05]));
  parts.push(paint(xf(cyl(0.9, 0.9, 0.04, 14), [0, 0.3, 0]), OUT.water, [1, 1]));
  for (const sx of [-1, 1]) parts.push(paint(xf(box(0.18, 2.4, 0.18), [sx * 1.05, 0.9, 0]), OUT.woodDark, [0.7, 1]));
  parts.push(paint(xf(new THREE.CylinderGeometry(0.08, 0.08, 2.3, 6).rotateZ(Math.PI / 2), [0, 2.55, 0]), OUT.wood, [1, 1]));
  for (const sz of [-1, 1]) parts.push(paint(xf(new THREE.BoxGeometry(2.8, 0.14, 1.3), [0, 3.55, sz * 0.55], [sz * 0.55, 0, 0]), OUT.roofRed, [0.75, 1.05]));
  parts.push(paint(xf(cyl(0.18, 0.15, 0.3, 8), [0.3, 1.8, 0]), OUT.wood, [0.7, 1]));
  void rnd;
  return combine(parts);
};

/** Lantern post: a dark post, an arm and a hanging lantern (its flame is a separate glow card). */
const lanternPost: Gen = () =>
  combine([
    paint(box(0.2, 3.1, 0.2), OUT.iron, [0.6, 1]),
    paint(xf(box(0.9, 0.1, 0.1), [0.4, 3.0, 0]), OUT.iron, [1, 1]),
    paint(xf(box(0.42, 0.55, 0.42), [0.75, 2.35, 0]), 0x2a2a30, [1, 1]),
    paint(xf(box(0.34, 0.42, 0.34), [0.75, 2.42, 0]), 0xffd890, [1.2, 1.3]),
    paint(xf(cone(0.34, 0.25, 4), [0.75, 2.9, 0], [0, Math.PI / 4, 0]), OUT.iron, [1, 1]),
    paint(cyl(0.22, 0.3, 0.3, 8), OUT.iron, [0.7, 1]),
  ]);

/** Banner pole with a long cloth (town square, bandit camp). */
function banner(cloth: number): Gen {
  return (rnd) => {
    const h = 4.6 + rnd() * 0.6;
    const parts = [paint(cyl(0.07, 0.1, h, 6), OUT.woodDark, [0.7, 1]), paint(xf(new THREE.CylinderGeometry(0.05, 0.05, 1.3, 6).rotateZ(Math.PI / 2), [0.55, h - 0.25, 0]), OUT.woodDark, [1, 1])];
    const c = new THREE.PlaneGeometry(1.1, 2.4, 2, 6);
    const p = c.attributes['position'] as THREE.BufferAttribute;
    for (let i = 0; i < p.count; i++) {
      const y = p.getY(i);
      p.setZ(i, Math.sin(y * 2.2 + rnd()) * 0.08);
      // Swallowtail cut at the bottom.
      if (y < -1.15 && Math.abs(p.getX(i)) < 0.1) p.setY(i, y + 0.4);
    }
    c.computeVertexNormals();
    const back = c.clone().scale(-1, 1, 1);
    back.computeVertexNormals();
    parts.push(paint(xf(c, [0.6, h - 1.5, 0.02]), cloth, [0.8, 1.05]), paint(xf(back, [0.6, h - 1.5, -0.02], [0, Math.PI, 0]), cloth, [0.7, 0.95]));
    parts.push(paint(xf(new THREE.OctahedronGeometry(0.14, 0), [0.6, h - 1.0, 0.05], [0, 0, 0], [1, 1, 0.3]), OUT.gold, [1.1, 1.2]));
    return combine(parts);
  };
}

/** Bounty / notice board: two posts, a roofed plank with pinned papers. */
const noticeBoard: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  for (const sx of [-1, 1]) parts.push(paint(xf(box(0.2, 2.9, 0.2), [sx * 1.15, 0, 0]), OUT.woodDark, [0.6, 1]));
  parts.push(paint(xf(box(2.6, 1.6, 0.14), [0, 0.95, 0]), OUT.wood, [0.85, 1.05]));
  for (const sz of [-1, 1]) parts.push(paint(xf(new THREE.BoxGeometry(3.0, 0.1, 0.7), [0, 2.95, sz * 0.22], [sz * 0.5, 0, 0]), OUT.roofRed, [0.8, 1.05]));
  const papers = [0xf4ead0, 0xe8dcc0, 0xf8f0dc, 0xeadab8, 0xf2e4c4];
  for (let i = 0; i < 6; i++) {
    const x = -0.9 + (i % 3) * 0.85 + (rnd() - 0.5) * 0.15;
    const y = 1.25 + Math.floor(i / 3) * 0.65 + (rnd() - 0.5) * 0.1;
    parts.push(paint(xf(box(0.5, 0.55, 0.02), [x, y - 0.27, 0.08], [0, 0, (rnd() - 0.5) * 0.25]), papers[i % papers.length]!, [1, 1]));
    parts.push(paint(xf(new THREE.IcosahedronGeometry(0.04, 0), [x, y + 0.2, 0.1]), 0xc03030, [1, 1]));
  }
  return combine(parts);
};

/** Dungeon board: a carved stone tablet with a rune circle (the glow is a separate card). */
const dungeonBoard: Gen = (rnd) => {
  const g = new THREE.BoxGeometry(2.0, 2.6, 0.45, 2, 2, 1);
  const p = g.attributes['position'] as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i++) if (p.getY(i) > 1.2) p.setX(i, p.getX(i) * 0.8);
  g.computeVertexNormals();
  return combine([paint(box(2.6, 0.4, 1.0), OUT.rockDark, [0.7, 1]), paint(xf(g, [0, 1.7, 0]), OUT.stone, [0.65, 1.05]), paint(xf(new THREE.TorusGeometry(0.55, 0.06, 5, 20), [0, 1.85, 0.24]), 0x5fd6d0, [1.1, 1.2]), paint(xf(rockGeo(0.5, rnd, 0.5), [1.3, 0, 0.3]), OUT.stoneMoss, [0.7, 1])]);
};

/** Anvil on a stump, plus a little forge hearth beside it. */
const anvil: Gen = () => {
  const parts: THREE.BufferGeometry[] = [paint(cyl(0.45, 0.55, 0.7, 9), OUT.bark, [0.6, 1])];
  parts.push(paint(xf(box(0.5, 0.25, 0.35), [0, 0.7, 0]), OUT.iron, [0.9, 1.1]));
  parts.push(paint(xf(box(0.95, 0.22, 0.45), [0, 0.95, 0]), 0x4a4a52, [1, 1.2]));
  parts.push(paint(xf(cone(0.18, 0.5, 6), [0.7, 1.06, 0], [0, 0, -Math.PI / 2]), 0x4a4a52, [1, 1.1]));
  // Hearth: stone box with a glowing coal bed.
  parts.push(paint(xf(box(1.5, 0.9, 1.2), [-1.7, 0, -0.2]), OUT.stone, [0.6, 1]));
  parts.push(paint(xf(box(1.1, 0.08, 0.8), [-1.7, 0.9, -0.2]), 0xff7a2a, [1.4, 1.6]));
  parts.push(paint(xf(box(0.5, 2.6, 0.5), [-1.7, 0.9, -0.6]), OUT.stone, [0.7, 1]));
  return combine(parts);
};

/** Bandit tent: a ridge tent of patched canvas on poles. */
const tent: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const W = 2.6;
  const L = 3.4;
  const H = 2.2;
  const half = W / 2;
  const slope = Math.hypot(half, H);
  const ang = Math.atan2(H, half);
  for (const sx of [-1, 1]) parts.push(paint(xf(new THREE.BoxGeometry(0.06, slope, L), [sx * half / 2, H / 2, 0], [0, 0, -sx * (Math.PI / 2 - ang)]), sx > 0 ? OUT.canvas : OUT.canvasDark, [0.75, 1.05]));
  for (const sz of [-1, 1]) parts.push(paint(xf(box(0.1, H + 0.3, 0.1), [0, 0, sz * (L / 2 + 0.05)]), OUT.woodDark, [0.7, 1]));
  parts.push(paint(xf(new THREE.CylinderGeometry(0.05, 0.05, L + 0.4, 5).rotateX(Math.PI / 2), [0, H + 0.02, 0]), OUT.woodDark, [1, 1]));
  parts.push(paint(xf(box(0.7, 0.6, 0.04), [0.4 * (rnd() < 0.5 ? -1 : 1), 0.9, L / 2 - 0.4], [0, 0, 0.3]), 0x7a5a3a, [1, 1]));
  // Back panel (closed end).
  const back = new THREE.BufferGeometry();
  back.setAttribute('position', new THREE.Float32BufferAttribute([-half, 0, -L / 2, half, 0, -L / 2, 0, H, -L / 2, half, 0, -L / 2, -half, 0, -L / 2, 0, H, -L / 2], 3));
  back.computeVertexNormals();
  parts.push(paint(back, OUT.canvasDark, [0.7, 0.95]));
  return combine(parts);
};

/** Campfire: a ring of stones, crossed logs (the flame is a separate card). */
const campfire: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    parts.push(paint(xf(rockGeo(0.22, rnd, 0.6), [Math.cos(a) * 0.75, 0, Math.sin(a) * 0.75]), OUT.rockDark, [0.7, 1]));
  }
  for (let i = 0; i < 3; i++) parts.push(paint(xf(new THREE.CylinderGeometry(0.08, 0.1, 1.1, 6).rotateZ(Math.PI / 2 - 0.3), [0, 0.18, 0], [0, (i / 3) * Math.PI, 0]), OUT.barkDark, [0.6, 1]));
  parts.push(paint(xf(cyl(0.5, 0.55, 0.05, 10), [0, 0, 0]), 0x2a2420, [1, 1]));
  return combine(parts);
};

/** Crate stack (bandit camp, market). */
const crates: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const n = 2 + Math.floor(rnd() * 2);
  for (let i = 0; i < n; i++) {
    const s = 0.8 + rnd() * 0.25;
    const x = (i % 2) * 0.9 - 0.45;
    const y = i >= 2 ? 0.85 : 0;
    parts.push(paint(xf(box(s, s, s), [x, y, (rnd() - 0.5) * 0.2], [0, rnd() * 0.5, 0]), OUT.wood, [0.65, 1.05]));
    parts.push(paint(xf(box(s + 0.04, 0.1, s + 0.04), [x, y + s * 0.5, 0], [0, 0, 0]), OUT.woodDark, [1, 1]));
  }
  return combine(parts);
};

const barrel: Gen = () => {
  const g = new THREE.CylinderGeometry(0.42, 0.42, 1.05, 12, 4);
  const p = g.attributes['position'] as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i++) {
    const y = p.getY(i) / 0.525;
    const k = 1 + 0.12 * (1 - y * y);
    p.setX(i, p.getX(i) * k);
    p.setZ(i, p.getZ(i) * k);
  }
  g.computeVertexNormals();
  return combine([paint(xf(g, [0, 0.525, 0]), OUT.wood, [0.65, 1.05]), paint(xf(new THREE.TorusGeometry(0.47, 0.035, 4, 14).rotateX(Math.PI / 2), [0, 0.25, 0]), OUT.iron, [1, 1]), paint(xf(new THREE.TorusGeometry(0.47, 0.035, 4, 14).rotateX(Math.PI / 2), [0, 0.8, 0]), OUT.iron, [1, 1])]);
};

const hayBale: Gen = (rnd) => {
  const g = new THREE.CylinderGeometry(0.6, 0.6, 1.2, 12).rotateZ(Math.PI / 2).translate(0, 0.6, 0);
  void rnd;
  return combine([paint(g, OUT.hay, [0.7, 1.08])]);
};

/** Signpost with two arrows. */
const signpost: Gen = () =>
  combine([
    paint(box(0.16, 2.6, 0.16), OUT.woodDark, [0.6, 1]),
    paint(xf(box(1.3, 0.32, 0.06), [0.45, 2.0, 0], [0, 0, 0.05]), OUT.wood, [1, 1]),
    paint(xf(box(1.2, 0.3, 0.06), [-0.4, 1.5, 0.02], [0, 0.4, -0.04]), OUT.wood, [1, 1]),
  ]);

/** A broken ruin wall piece (meadow landmarks, the barrow approach). */
const ruinWall: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 5; i++) {
    const h = 0.9 + rnd() * 1.8 * (i === 1 || i === 2 ? 1.4 : 0.8);
    parts.push(paint(xf(box(0.95, h, 0.8), [-2 + i * 1.0, 0, (rnd() - 0.5) * 0.15], [0, (rnd() - 0.5) * 0.1, 0]), i % 2 ? OUT.stone : OUT.stoneMoss, [0.6, 1.05]));
  }
  parts.push(paint(xf(rockGeo(0.5, rnd, 0.5), [1.4, 0, 0.9]), OUT.stone, [0.7, 1]));
  return combine(parts);
};

/**
 * The barrow mound (starter-dungeon entrance): a grassy, half-sunken hill with a stone doorway
 * (two jambs, a heavy lintel, a stepped threshold going down into the dark) facing +z. The swirl,
 * crystals, standing stones, shafts and motes are separate (outdoor/portals.ts).
 */
const barrowMound: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  // Mound: a squashed lumpy dome, grass on top, earth on the flanks.
  const dome = blob(6.0, rnd, 0.08, 18, 10, 0.05);
  dome.scale(1.0, 0.72, 0.95);
  dome.translate(0, 0.1, -3.9);
  parts.push(paint(dome, 0x5f9e45, [0.55, 1.12], [-1, 4.4]));
  // Earthy flanks where the turf is worn away around the stones.
  parts.push(paint(xf(blob(2.2, rnd, 0.2, 9, 6, 0.4), [-2.9, 0.2, 0.9], [0, 0, 0], [1, 0.55, 1]), 0x7a6a4e, [0.6, 1.0]));
  parts.push(paint(xf(blob(2.2, rnd, 0.2, 9, 6, 0.4), [2.9, 0.2, 0.9], [0, 0, 0], [1, 0.55, 1]), 0x7a6a4e, [0.6, 1.0]));
  // Stone facade around the mouth.
  parts.push(paint(xf(box(1.3, 3.6, 1.2), [-2.1, -0.2, 2.6], [0, 0, 0.03]), OUT.stone, [0.6, 1.05]));
  parts.push(paint(xf(box(1.3, 3.5, 1.2), [2.1, -0.2, 2.6], [0, 0, -0.03]), OUT.stone, [0.6, 1.05]));
  parts.push(paint(xf(box(5.8, 1.1, 1.5), [0, 3.25, 2.55], [0, 0, 0.02]), OUT.stoneMoss, [0.7, 1.05]));
  parts.push(paint(xf(box(6.4, 0.5, 1.7), [0, 4.2, 2.4]), OUT.stone, [0.85, 1.05]));
  // Dark mouth (a recessed back wall) and steps going down.
  parts.push(paint(xf(box(3.0, 3.4, 0.2), [0, -0.6, 0.6]), 0x0e1416, [1, 1]));
  for (let i = 0; i < 4; i++) parts.push(paint(xf(box(3.0, 0.25, 0.7), [0, -0.25 - i * 0.22, 2.9 - i * 0.6]), i % 2 ? OUT.stone : OUT.rockDark, [0.6 - i * 0.08, 0.9 - i * 0.1]));
  // Side retaining stones half buried in the mound.
  for (const sx of [-1, 1])
    for (let i = 0; i < 3; i++) parts.push(paint(xf(rockGeo(0.8, rnd, 0.8), [sx * (3.3 + i * 0.9), 0, 2.0 - i * 0.9]), OUT.stone, [0.6, 1]));
  // Carved rune band on the lintel (bright albedo; the glow is the rune card).
  parts.push(paint(xf(box(3.4, 0.2, 0.05), [0, 3.55, 3.32]), 0x7fe8de, [1.1, 1.2]));
  return combine(parts);
};

/** Portal ring: a circle of rough stones standing on edge (a field portal frame), facing +z. */
const portalRing: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  const n = 11;
  for (let i = 0; i < n; i++) {
    const a = (i / (n - 1)) * Math.PI;
    const x = Math.cos(a) * 2.3;
    const y = Math.sin(a) * 2.9;
    const s = 0.55 + rnd() * 0.15;
    parts.push(paint(xf(rockGeo(s, rnd, 1.0), [x, y - s * 0.4, 0], [0, 0, a - Math.PI / 2], [0.8, 1.2, 0.7]), i % 3 ? OUT.stone : OUT.stoneMoss, [0.7, 1.05]));
  }
  parts.push(paint(xf(box(5.6, 0.25, 1.6), [0, 0, 0]), OUT.rockDark, [0.8, 1]));
  for (const sx of [-1, 1]) parts.push(paint(xf(rockGeo(0.6, rnd, 0.7), [sx * 2.9, 0, 0.4]), OUT.stoneMoss, [0.7, 1]));
  return combine(parts);
};

/** Gate (portal kind 'gate'): two stone-footed timber posts, a cross beam with a hanging sign. */
const gateArch: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  for (const sx of [-1, 1]) {
    parts.push(paint(xf(rockGeo(0.55, rnd, 0.7), [sx * 2.5, 0, 0]), OUT.stone, [0.7, 1]));
    parts.push(paint(xf(cyl(0.2, 0.26, 4.1, 7), [sx * 2.5, 0.2, 0]), OUT.timber, [0.65, 1]));
    parts.push(paint(xf(new THREE.OctahedronGeometry(0.22, 0), [sx * 2.5, 4.4, 0]), OUT.gold, [1, 1.15]));
  }
  parts.push(paint(xf(new THREE.CylinderGeometry(0.14, 0.14, 5.6, 7).rotateZ(Math.PI / 2), [0, 3.85, 0]), OUT.timber, [0.85, 1]));
  for (const sx of [-0.55, 0.55]) parts.push(paint(xf(box(0.04, 0.5, 0.04), [sx, 3.3, 0]), OUT.iron, [1, 1]));
  parts.push(paint(xf(box(1.6, 0.55, 0.08), [0, 2.85, 0]), OUT.wood, [0.85, 1.05]));
  // Garland of leaves along the beam.
  for (let i = 0; i < 7; i++) parts.push(paint(xf(blob(0.24, rnd, 0.3, 6, 4, 0), [-2.1 + i * 0.7, 3.7 - Math.sin((i / 6) * Math.PI) * 0.25, 0.1]), i % 2 ? OUT.leafMeadow : OUT.leafMeadowLight, [0.8, 1.05]));
  return combine(parts);
};

/** Root tangle (barrow interior walls / forest floor). */
const roots: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 5; i++) {
    const pts: THREE.Vector3[] = [];
    const a = rnd() * Math.PI * 2;
    let x = 0;
    let y = 1.6 + rnd() * 1.2;
    let z = 0;
    for (let k = 0; k < 5; k++) {
      pts.push(new THREE.Vector3(x, y, z));
      x += Math.cos(a) * (0.35 + rnd() * 0.3);
      z += Math.sin(a) * (0.35 + rnd() * 0.3);
      y = Math.max(0, y - 0.45 - rnd() * 0.2);
    }
    const tube = new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 10, 0.09 + rnd() * 0.06, 5, false);
    parts.push(paint(tube, OUT.barkDark, [0.6, 1.0]));
  }
  return combine(parts);
};


// ---------------------------------------------------------------------------------------------
// More field props (content kinds) and the Hollow Barrow interior pieces
// ---------------------------------------------------------------------------------------------

/** Windmill: a stone tower, a conical roof and four lattice sails. */
const windmill: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [paint(cyl(2.0, 2.6, 7.5, 10), OUT.plaster, [0.7, 1.05]), paint(xf(cone(2.6, 3.0, 10), [0, 7.5, 0]), OUT.roofThatch, [0.75, 1.05])];
  parts.push(paint(xf(box(1.0, 1.9, 0.2), [0, 0, 2.45]), OUT.woodDark, [0.7, 1]));
  parts.push(paint(xf(box(0.7, 0.7, 0.1), [0, 4.2, 2.25]), 0x2c3a52, [1, 1]));
  parts.push(paint(xf(new THREE.CylinderGeometry(0.25, 0.25, 1.2, 8).rotateX(Math.PI / 2), [0, 7.2, 2.4]), OUT.woodDark, [1, 1]));
  const spin = rnd() * 0.8;
  for (let i = 0; i < 4; i++) {
    const a = spin + (i * Math.PI) / 2;
    const arm = xf(box(0.22, 5.2, 0.12), [0, 0, 0]);
    const sail = xf(box(1.3, 3.6, 0.05), [0.75, 1.4, 0]);
    for (const g of [arm, sail]) {
      g.rotateZ(a).translate(0, 7.2, 3.0);
      parts.push(paint(g, g === sail ? OUT.clothCream : OUT.wood, [0.85, 1.05]));
    }
  }
  return combine(parts);
};

/** Watchtower: four timber legs, a railed platform, a little roof, a ladder. */
const watchtower: Gen = () => {
  const parts: THREE.BufferGeometry[] = [];
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) parts.push(paint(xf(box(0.3, 6.5, 0.3), [sx * 1.4, 0, sz * 1.4], [sz * 0.06, 0, -sx * 0.06]), OUT.woodDark, [0.6, 1]));
  parts.push(paint(xf(box(3.6, 0.25, 3.6), [0, 5.0, 0]), OUT.wood, [0.8, 1]));
  for (const [x, z, w, d] of [[0, 1.75, 3.6, 0.12], [0, -1.75, 3.6, 0.12], [1.75, 0, 0.12, 3.6], [-1.75, 0, 0.12, 3.6]] as const) parts.push(paint(xf(box(w, 1.0, d), [x, 5.25, z]), OUT.wood, [0.85, 1]));
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) parts.push(paint(xf(box(0.16, 2.0, 0.16), [sx * 1.6, 5.25, sz * 1.6]), OUT.woodDark, [0.8, 1]));
  parts.push(paint(xf(cone(2.9, 1.6, 4), [0, 7.25, 0], [0, Math.PI / 4, 0]), 0x6a3a2a, [0.8, 1.05]));
  for (let i = 0; i < 9; i++) parts.push(paint(xf(box(0.9, 0.08, 0.1), [0, 0.5 + i * 0.55, 1.95]), OUT.wood, [1, 1]));
  for (const sx of [-1, 1]) parts.push(paint(xf(box(0.1, 5.2, 0.1), [sx * 0.45, 0, 1.95]), OUT.woodDark, [0.8, 1]));
  return combine(parts);
};

/** Palisade: a row of sharpened logs (3 yd along x). */
const palisade: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 7; i++) {
    const h = 2.6 + rnd() * 0.6;
    const x = -1.5 + i * 0.5;
    parts.push(paint(xf(cyl(0.22, 0.24, h, 7), [x, 0, (rnd() - 0.5) * 0.1]), OUT.bark, [0.6, 1]));
    parts.push(paint(xf(cone(0.22, 0.5, 7), [x, h, 0]), 0xb08a5e, [0.9, 1.05]));
  }
  parts.push(paint(xf(box(3.4, 0.16, 0.12), [0, 1.6, 0.24]), OUT.woodDark, [1, 1]));
  return combine(parts);
};

/** Farm cart with two wheels and a few sacks. */
const cart: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [paint(xf(box(1.6, 0.6, 2.6), [0, 0.7, 0]), OUT.wood, [0.7, 1.05])];
  for (const sx of [-1, 1]) {
    parts.push(paint(xf(new THREE.TorusGeometry(0.55, 0.09, 6, 14).rotateY(Math.PI / 2), [sx * 0.9, 0.6, -0.2]), OUT.woodDark, [0.8, 1]));
    parts.push(paint(xf(new THREE.CylinderGeometry(0.08, 0.08, 0.2, 6).rotateZ(Math.PI / 2), [sx * 0.9, 0.6, -0.2]), OUT.iron, [1, 1]));
  }
  for (const sx of [-1, 1]) parts.push(paint(xf(box(0.08, 0.08, 1.6), [sx * 0.3, 0.75, 2.0]), OUT.woodDark, [1, 1]));
  for (let i = 0; i < 3; i++) parts.push(paint(xf(blob(0.32, rnd, 0.15, 7, 5, 0.3), [(rnd() - 0.5) * 0.8, 1.35, -0.6 + i * 0.55], [0, 0, 0], [1, 0.85, 1.2]), 0xcbb488, [0.75, 1.05]));
  return combine(parts);
};

const bench: Gen = () =>
  combine([
    paint(xf(box(2.0, 0.12, 0.5), [0, 0.5, 0]), OUT.wood, [0.9, 1.05]),
    paint(xf(box(2.0, 0.5, 0.1), [0, 0.6, -0.25], [-0.15, 0, 0]), OUT.wood, [0.9, 1.05]),
    ...[-0.85, 0.85].map((x) => paint(xf(box(0.12, 0.5, 0.45), [x, 0, 0]), OUT.woodDark, [0.7, 1])),
  ]);

/** A broken column (ruin_pillar). */
const ruinPillar: Gen = (rnd) => {
  const h = 2.4 + rnd() * 1.4;
  const g = new THREE.CylinderGeometry(0.55, 0.62, h, 10, 2);
  const p = g.attributes['position'] as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i++) if (p.getY(i) > h / 2 - 0.01) p.setY(i, p.getY(i) - rnd() * 0.6);
  g.computeVertexNormals();
  return combine([paint(box(1.6, 0.4, 1.6), OUT.stone, [0.65, 1]), paint(xf(g, [0, 0.4 + h / 2, 0]), OUT.stone, [0.65, 1.05]), paint(xf(rockGeo(0.45, rnd, 0.6), [0.9, 0, 0.6]), OUT.stoneMoss, [0.7, 1])]);
};

/** Forge: a stone hearth under a timber lean-to roof with a chimney (glow is a light). */
const forge: Gen = () => {
  const parts: THREE.BufferGeometry[] = [paint(box(2.2, 1.0, 1.6), OUT.stone, [0.6, 1]), paint(xf(box(1.6, 0.08, 1.1), [0, 1.0, 0.1]), 0xff7a2a, [1.4, 1.6])];
  parts.push(paint(xf(box(0.9, 4.2, 0.9), [0, 1.0, -0.45]), OUT.stone, [0.65, 1.05]));
  for (const sx of [-1, 1]) parts.push(paint(xf(box(0.2, 3.0, 0.2), [sx * 1.9, 0, 1.4]), OUT.timber, [0.7, 1]));
  parts.push(paint(xf(new THREE.BoxGeometry(4.4, 0.14, 3.0), [0, 3.2, 0.2], [0.25, 0, 0]), OUT.roofRed, [0.8, 1.05]));
  parts.push(paint(xf(cyl(0.3, 0.3, 0.9, 8), [1.5, 0, 0.9]), OUT.wood, [0.7, 1]));
  return combine(parts);
};

/** Barrow interior: a squared stone pillar with a glowing rune band and mossy foot. */
const runePillar: Gen = (rnd) => {
  const parts: THREE.BufferGeometry[] = [paint(box(2.1, 0.6, 2.1), OUT.rockDark, [0.6, 0.95]), paint(xf(box(1.6, 6.2, 1.6), [0, 0.6, 0]), 0x8d978d, [0.55, 1.05])];
  parts.push(paint(xf(box(1.68, 0.32, 1.68), [0, 2.4, 0]), 0x6ff0dc, [1.5, 1.6]));
  parts.push(paint(xf(box(1.68, 0.12, 1.68), [0, 3.0, 0]), 0x6ff0dc, [1.3, 1.4]));
  parts.push(paint(xf(box(2.0, 0.5, 2.0), [0, 6.6, 0]), 0x7d877d, [0.85, 1.05]));
  parts.push(paint(xf(blob(1.1, rnd, 0.25, 8, 5, 0.6), [0.2, 0.55, 0.3], [0, 0, 0], [1.1, 0.4, 1.0]), 0x587a44, [0.8, 1.05]));
  return combine(parts);
};

/** Barrow throne: stepped dais, a high-backed stone seat, two rune stones. */
const throne: Gen = () => {
  const parts: THREE.BufferGeometry[] = [paint(box(4.6, 0.4, 3.4), OUT.rockDark, [0.6, 0.9]), paint(xf(box(3.8, 0.4, 2.8), [0, 0.4, -0.2]), 0x7d877d, [0.7, 1])];
  parts.push(paint(xf(box(1.8, 0.7, 1.3), [0, 0.8, -0.6]), 0x8d978d, [0.7, 1.05]));
  parts.push(paint(xf(box(2.0, 3.6, 0.5), [0, 0.8, -1.3]), 0x8d978d, [0.6, 1.05]));
  parts.push(paint(xf(cone(1.1, 1.2, 4), [0, 4.4, -1.3], [0, Math.PI / 4, 0], [1, 1, 0.35]), 0x7d877d, [0.8, 1]));
  for (const sx of [-1, 1]) parts.push(paint(xf(box(0.4, 0.9, 1.3), [sx * 0.95, 0.8, -0.6]), 0x7d877d, [0.7, 1]));
  parts.push(paint(xf(new THREE.TorusGeometry(0.45, 0.07, 5, 16), [0, 3.3, -1.03]), 0x6ff0dc, [1.5, 1.6]));
  parts.push(paint(xf(new THREE.OctahedronGeometry(0.25, 0), [0, 3.3, -1.0]), OUT.gold, [1.2, 1.3]));
  return combine(parts);
};

/** A crypt door jamb: a tall carved stone post with a rune slot (two flank a corridor mouth). */
const cryptJamb: Gen = () =>
  combine([
    paint(box(1.3, 0.5, 1.3), OUT.rockDark, [0.6, 0.9]),
    paint(xf(box(1.0, 5.0, 1.0), [0, 0.5, 0]), 0x8d978d, [0.55, 1.05]),
    paint(xf(box(0.3, 1.6, 1.04), [0, 2.4, 0]), 0x6ff0dc, [1.4, 1.5]),
    paint(xf(box(1.4, 0.45, 1.4), [0, 5.5, 0]), 0x7d877d, [0.85, 1.05]),
  ]);

/** A low stone plinth (barrow lantern plinths). */
const plinth: Gen = () => combine([paint(box(2.2, 0.4, 2.2), OUT.rockDark, [0.6, 0.9]), paint(xf(box(1.7, 1.1, 1.7), [0, 0.4, 0]), 0x8d978d, [0.6, 1.05]), paint(xf(box(1.9, 0.18, 1.9), [0, 1.5, 0]), 0x7d877d, [0.9, 1.05])]);

const GEN: Record<string, Gen> = {
  tree_round: roundTree(OUT.leafMeadow, OUT.leafMeadowLight, 6.2),
  tree_small: roundTree(OUT.leafMeadowLight, OUT.leafMeadow, 4.4),
  tree_blossom: roundTree(OUT.blossom, 0xf8c6d8, 5.6),
  tree_oak: oakTree(7.4),
  tree_oak_big: oakTree(9.0),
  pine: pineTree(7.5),
  pine_tall: pineTree(9.5),
  bush: bush(OUT.leafMeadow, OUT.leafMeadowLight, 0.75),
  bush_dark: bush(OUT.leafForest, OUT.leafForestDark, 0.85),
  flowers: flowers([0xfff2a0, 0xffffff, 0xf2a0c8, 0xf2d048]),
  flowers_blue: flowers([0x8ab8ff, 0xffffff, 0xc8a0ff]),
  flowers_red: flowers([0xff6a5a, 0xffd060, 0xffffff]),
  bramble,
  stump,
  log,
  mushrooms,
  rock_small: rock(0.45, 0.55),
  rock: rock(0.9, 0.6),
  rock_big: rock(1.6, 0.65),
  boulder,
  cliff_rock: rock(2.6, 0.9, OUT.rockDark),
  standing_stone: standingStone,
  crystals,
  fence,
  hedge,
  cottage_red: cottage(OUT.roofRed, OUT.plaster),
  cottage_slate: cottage(OUT.roofSlate, OUT.plasterWarm),
  cottage_thatch: cottage(OUT.roofThatch, OUT.plaster),
  stall_red: stall(OUT.cloth, OUT.clothCream),
  stall_blue: stall(OUT.clothBlue, OUT.clothCream),
  well,
  lantern_post: lanternPost,
  banner_red: banner(OUT.cloth),
  banner_blue: banner(OUT.clothBlue),
  banner_bandit: banner(0x5a2a2a),
  notice_board: noticeBoard,
  dungeon_board: dungeonBoard,
  anvil,
  tent,
  campfire,
  crates,
  barrel,
  hay_bale: hayBale,
  signpost,
  ruin_wall: ruinWall,
  barrow_mound: barrowMound,
  portal_ring: portalRing,
  gate_arch: gateArch,
  roots,
  windmill,
  watchtower,
  palisade,
  cart,
  bench,
  ruin_pillar: ruinPillar,
  forge,
  rune_pillar: runePillar,
  throne,
  crypt_jamb: cryptJamb,
  plinth,
};

/** Generators registered by other modules (+valley: outdoor/valley.ts, the Sunmeadow art kit). */
const EXTRA: Record<string, Gen> = {};
export function registerOutdoorProcs(gens: Record<string, Gen>): void {
  Object.assign(EXTRA, gens);
}

/** Every built-in generator name (tests). */
export const OUTDOOR_PROCS = Object.keys(GEN);

/**
 * Geometry for `name` or `name#variant` (cached per id; variants reseed the shape). Null for an
 * unknown name. The cache is the caller's (per field build) so geometries are disposed with it.
 */
export function outdoorProc(id: string, cache: Map<string, THREE.BufferGeometry | null>, cacheKey = id): THREE.BufferGeometry | null {
  if (cache.has(cacheKey)) return cache.get(cacheKey)!;
  const hash = id.indexOf('#');
  const name = hash < 0 ? id : id.slice(0, hash);
  const gen = GEN[name] ?? EXTRA[name];
  const g = gen ? gen(seededRandom(hashString(id))) : null;
  cache.set(cacheKey, g);
  return g;
}
